import { useCallback, useEffect, useState } from 'react'
import { api } from '../../lib/api'
import type { ReportView } from '../../lib/types'
import { useStaffText } from '../../ui/StaffText'
import { Bookings } from '../Bookings'
import { Breaks } from '../Breaks'
import { Estimates } from '../Estimates'
import { isoToday, type PeriodId } from '../insightCalc'
import { Crumbs as Steps } from '../kit'
import type { TicketFilters } from '../ticketReport'
import { Tickets } from '../Tickets'
import { MailPanel } from './ReportsList'
import type { ReportsCtx } from './ReportsPage'
import '../../styles/insight.css'

type Names = Partial<Record<keyof TicketFilters, string>>

// A booking figure clicked through to its tickets: handed to the Tickets page once.
let drilled: { filters: TicketFilters; names: Names } | null = null

function Crumbs({ ctx, name }: { ctx: ReportsCtx; name: string }) {
  return <Steps name={name} onBack={() => ctx.go('/reports')} />
}

/** The built-in Tickets report: one row per piece of work, with its own saved filters. */
export function TicketsPage({ ctx }: { ctx: ReportsCtx }) {
  const { say } = useStaffText()
  const [drill] = useState(() => {
    const d = drilled
    drilled = null
    return d
  })
  const [views, setViews] = useState<ReportView[]>([])
  const [ready, setReady] = useState(false)
  const [problem, setProblem] = useState(false)
  const [mail, setMail] = useState(false)
  const branches = ctx.branches ?? []

  const reload = useCallback(() => {
    api.reportViews(ctx.organisation).then(
      (r) => {
        setViews(r.views.filter((v) => v.kind === 'tickets'))
        setReady(true)
        setProblem(false)
      },
      () => setProblem(true),
    )
  }, [ctx.organisation])
  useEffect(reload, [reload])

  return (
    <main className="page rp-page rp-classic">
      <Crumbs ctx={ctx} name={say('pivot.bi.tickets')} />
      {ctx.branches && (
        <Tickets
          branches={branches}
          branchId={branches[0]?.id ?? ''}
          lead={null}
          onMail={() => setMail(true)}
          views={views}
          viewsReady={ready}
          viewsProblem={problem ? <span className="rp-muted">{say('common.unreachable')}</span> : undefined}
          onViews={(next) => setViews(next.filter((v) => v.kind === 'tickets'))}
          onReloadViews={reload}
          organisation={ctx.organisation}
          toast={ctx.toast}
          initial={drill?.filters}
          names={drill?.names}
          figure={drill ? 'tickets' : undefined}
          onFigures={drill ? () => ctx.go('/reports/bookings') : undefined}
          backLabel={drill ? say('pivot.bi.bookings') : undefined}
        />
      )}
      {mail && <MailPanel ctx={ctx} onClose={() => setMail(false)} />}
    </main>
  )
}

/** The built-in Wait estimates report: waits told against waits had. */
export function EstimatesPage({ ctx }: { ctx: ReportsCtx }) {
  const { say } = useStaffText()
  return (
    <main className="page rp-page rp-classic">
      <Crumbs ctx={ctx} name={say('pivot.bi.estimates')} />
      {ctx.branches && <Estimates branches={ctx.branches} />}
    </main>
  )
}

/** The built-in Breaks report: every lunch, short break and task that ran. */
export function BreaksPage({ ctx }: { ctx: ReportsCtx }) {
  const { say } = useStaffText()
  return (
    <main className="page rp-page rp-classic">
      <Crumbs ctx={ctx} name={say('pivot.bi.breaks')} />
      {ctx.branches && <Breaks branches={ctx.branches} />}
    </main>
  )
}

/** The built-in Bookings report. */
export function BookingsPage({ ctx }: { ctx: ReportsCtx }) {
  const { say } = useStaffText()
  const today = isoToday()
  const branches = ctx.branches ?? []
  const [branchId, setBranchId] = useState('')
  const [period, setPeriod] = useState<PeriodId>('week')
  const [custom, setCustom] = useState<[string, string]>([today, today])
  const [mail, setMail] = useState(false)
  const shown = branchId || branches[0]?.id || ''

  return (
    <main className="page rp-page rp-classic">
      <Crumbs ctx={ctx} name={say('pivot.bi.bookings')} />
      {ctx.branches && (
        <Bookings
          active
          branches={branches}
          branchId={shown}
          onBranch={setBranchId}
          period={period}
          onPeriod={setPeriod}
          custom={custom}
          onCustom={setCustom}
          lead={null}
          onMail={() => setMail(true)}
          onRead={() => undefined}
          onDrill={(filters, names) => {
            drilled = { filters, names }
            ctx.go('/reports/tickets')
          }}
        />
      )}
      {mail && <MailPanel ctx={ctx} branchId={shown} onClose={() => setMail(false)} />}
    </main>
  )
}
